import React, { useContext, useLayoutEffect, useRef } from 'react';
import { STATIC_EXECUTION_CONTEXT } from '../constants';
import GlobalStyle from '../models/GlobalStyle';
import { useStyleSheet, useStylis } from '../models/StyleSheetManager';
import { Theme, ThemeContext } from '../models/ThemeProvider';
import StyleSheet from '../sheet';
import { ExtensibleObject, Interpolation, RuleSet, Stringifier, Styles } from '../types';
import { checkDynamicCreation } from '../utils/checkDynamicCreation';
import determineTheme from '../utils/determineTheme';
import generateComponentId from '../utils/generateComponentId';
import css from './css';

export default function createGlobalStyle(
  strings: Styles,
  ...interpolations: Array<Interpolation>
) {
  const rules = css(strings, ...interpolations) as RuleSet;
  const styledComponentId = `sc-global-${generateComponentId(JSON.stringify(rules))}`;
  const globalStyle = new GlobalStyle(rules, styledComponentId);

  if (process.env.NODE_ENV !== 'production') {
    checkDynamicCreation(styledComponentId);
  }

  const GlobalStyleComponent: React.ComponentType<ExtensibleObject> = props => {
    const styleSheet = useStyleSheet();
    const stylis = useStylis();
    const theme = useContext(ThemeContext);
    const instanceRef = useRef(styleSheet.allocateGSInstance(styledComponentId));

    const instance = instanceRef.current;

    if (process.env.NODE_ENV !== 'production' && React.Children.count(props.children)) {
      // eslint-disable-next-line no-console
      console.warn(
        `The global style component ${styledComponentId} was given child JSX. createGlobalStyle does not render children.`
      );
    }

    if (
      process.env.NODE_ENV !== 'production' &&
      rules.some(rule => typeof rule === 'string' && rule.indexOf('@import') !== -1)
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `Please do not use @import CSS syntax in createGlobalStyle at this time, as the CSSOM APIs we use in production do not handle it well. Instead, we recommend using a library such as react-helmet to inject a typical <link> meta tag to the stylesheet, or simply embedding it manually in your index.html <head> section for a simpler app.`
      );
    }

    if (styleSheet.server) {
      renderStyles(instance, props, styleSheet, theme, stylis);
    }

    if (!__SERVER__) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useLayoutEffect(() => {
        if (!styleSheet.server) {
          renderStyles(instance, props, styleSheet, theme, stylis);
          return () => globalStyle.removeStyles(instance, styleSheet);
        }
      }, [instance, props, styleSheet, theme, stylis]);
    }

    return null;
  };

  function renderStyles(
    instance: number,
    props: ExtensibleObject,
    styleSheet: StyleSheet,
    theme: Theme | undefined,
    stylis: Stringifier
  ) {
    if (globalStyle.isStatic) {
      globalStyle.renderStyles(instance, STATIC_EXECUTION_CONTEXT, styleSheet, stylis);
    } else {
      const context = {
        ...props,
        theme: determineTheme(props, theme, GlobalStyleComponent.defaultProps),
      };

      globalStyle.renderStyles(instance, context, styleSheet, stylis);
    }
  }

  return React.memo(GlobalStyleComponent);
}
